import { motion } from "framer-motion";

const PrivacyPolicyPage = () => {
  return (
    <div>
      <section className="bg-warm-800 text-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              Privacy Policy
            </h1>
            <p className="text-warm-200">Last updated: February 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 prose prose-gray">
          <div className="space-y-8 text-sm text-gray-700 leading-relaxed">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>1. Overview</h2>
              <p>The Pillar Project Corporation ("we," "our," "us") is a registered 501(c)(3) nonprofit organization committed to protecting the privacy and safety of all individuals who use our services. This policy describes how we collect, use, store, and protect personal information.</p>
              <p className="mt-2">Given the nature of our work with survivors of domestic violence, human trafficking, and sexual assault, we maintain the highest standards of data privacy and confidentiality.</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>2. Information We Collect</h2>
              <p><strong>Survivor/Client Information:</strong> Name, contact information, case details, and service records collected during intake and advocacy. This information is encrypted at rest using AES-256 encryption.</p>
              <p className="mt-2"><strong>Partner/Agency Information:</strong> Organization name, contact person, credentials, and referral data submitted through our Partner Portal.</p>
              <p className="mt-2"><strong>LAP Report Data:</strong> Lethality Assessment Program reports containing victim and incident information, submitted by law enforcement and agency partners. All sensitive fields are encrypted at the field level.</p>
              <p className="mt-2"><strong>Website Visitors:</strong> Basic analytics data. We do not use tracking cookies for advertising.</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>3. How We Use Information</h2>
              <p>We use collected information solely to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Provide crisis intervention and advocacy services</li>
                <li>Coordinate emergency resources (shelter, legal aid, medical care)</li>
                <li>Manage cases and track service delivery</li>
                <li>Generate anonymized, aggregate reports for grant compliance</li>
                <li>Communicate with clients about their services</li>
              </ul>
              <p className="mt-2">We never sell, share, or disclose personal information to third parties for commercial purposes.</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>4. Data Security</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Encryption:</strong> Sensitive fields (victim names, contact info, addresses, incident details) are encrypted using AES-256-GCM at the field level before storage</li>
                <li><strong>Access Control:</strong> Role-based access control (RBAC) restricts data access to authorized personnel only</li>
                <li><strong>Multi-Factor Authentication:</strong> Required for all admin and partner accounts accessing sensitive data</li>
                <li><strong>Audit Logging:</strong> Every access, modification, and export of sensitive data is logged with timestamp, user identity, and IP address</li>
                <li><strong>Session Security:</strong> 15-minute idle timeout, single-session enforcement</li>
                <li><strong>HTTPS:</strong> All data in transit is encrypted via TLS</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>5. Data Retention</h2>
              <p>We follow a structured data retention policy:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Active:</strong> Records 0 to 2 years old are actively maintained for ongoing service delivery</li>
                <li><strong>Archived:</strong> Records 2 to 7 years old are archived with restricted access for grant compliance and legal requirements</li>
                <li><strong>Deleted:</strong> Records older than 7 years are permanently and irreversibly deleted</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Request access to your personal information</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data (subject to legal retention requirements)</li>
                <li>Withdraw consent for non-essential data processing</li>
              </ul>
              <p className="mt-2">To exercise these rights, contact director@thepillarprojectbaltimore.org.</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>7. Mandatory Reporting</h2>
              <p>As a social services organization, we are required by Maryland law to report suspected child abuse or neglect. We may also be compelled to disclose information pursuant to a valid court order. We will notify affected individuals when legally permitted to do so.</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>8. Breach Response</h2>
              <p>In the event of a data breach involving personal information, we will:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Notify affected individuals within 72 hours of discovery</li>
                <li>Report to relevant authorities as required by Maryland law</li>
                <li>Conduct a full investigation and implement corrective measures</li>
                <li>Provide a written incident report</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>9. Legal Framework & Compliance</h2>
              <p>Our data practices are informed by:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Violence Against Women Act (VAWA):</strong> We adhere to VAWA confidentiality provisions, which prohibit the disclosure of personally identifying information collected in connection with services to victims of domestic violence, sexual assault, stalking, and trafficking.</li>
                <li><strong>Maryland Personal Information Protection Act:</strong> We comply with Maryland's breach notification requirements and data protection standards.</li>
                <li><strong>Victims of Crime Act (VOCA):</strong> Data collected under VOCA-funded programs follows VOCA confidentiality and reporting requirements.</li>
                <li><strong>Family Violence Prevention and Services Act (FVPSA):</strong> Client information is protected under FVPSA confidentiality mandates.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>10. Consent Workflows</h2>
              <p>We obtain informed consent before collecting any personal information:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Referral Consent:</strong> Partners submitting referrals and LAP reports confirm they have authority to share client information for the purpose of coordinating services.</li>
                <li><strong>Intake Consent:</strong> Survivors provide informed consent during intake, acknowledging what information is collected, how it will be used, who will have access, and their right to withdraw consent.</li>
                <li><strong>Service Enrollment:</strong> Education and training program enrollments include a data consent statement.</li>
                <li><strong>Withdrawal:</strong> Clients may withdraw consent at any time by contacting director@thepillarprojectbaltimore.org. Withdrawal does not affect the lawfulness of processing conducted prior to withdrawal.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>11. Justification for Storing Full LAP Data</h2>
              <p>We store complete Lethality Assessment Program data (including victim PII) for the following documented reasons:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Immediate Safety:</strong> Contact information is required to reach survivors within 24 hours and coordinate emergency resources.</li>
                <li><strong>Resource Coordination:</strong> Incident details enable accurate safety planning.</li>
                <li><strong>Continuity of Care:</strong> Complete records enable effective warm hand-offs to long-term service partners.</li>
                <li><strong>Grant Compliance:</strong> Funders require outcome tracking that links intake data to service delivery.</li>
                <li><strong>Law Enforcement Collaboration:</strong> Receiving organizations need full context for effective intervention.</li>
              </ul>
              <p className="mt-2">All stored LAP data is encrypted at the field level (AES-256-GCM), access-controlled, audit-logged, and subject to our data retention lifecycle (active 0 to 2 years, archived 2 to 7 years, permanently deleted after 7 years).</p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>12. Data Deletion Enforcement</h2>
              <p>Data deletion is enforced through automated and manual controls:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Records older than 2 years are automatically flagged for archival with restricted access.</li>
                <li>Records older than 7 years are permanently deleted upon admin review and confirmation.</li>
                <li>All deletion actions are irreversible and audit-logged.</li>
                <li>Individual data deletion requests are processed within 30 days, subject to legal retention obligations.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>13. Contact</h2>
              <p>For privacy-related inquiries:</p>
              <p className="mt-2">The Pillar Project Corporation<br />Baltimore, Maryland<br />(347) 221-4189<br />director@thepillarprojectbaltimore.org</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
